import { lazy, Suspense, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight,
  Globe,
  Network,
  Mail,
  Fingerprint,
  User,
  Terminal,
  BookOpen,
  Shield,
  Hash,
  Code2,
  Image as ImageIcon,
  Wrench,
  Eye,
  Lock,
  Zap,
  Activity,
  Radio,
  Cpu,
  ChevronRight,
  GraduationCap,
} from "lucide-react";
import {
  ScanSearch,
  Radar,
  AtSign,
  MailSearch,
  UserSearch,
  Binary,
  FileDigit,
  ScanLine,
  SquareTerminal,
} from "lucide-react";
import { AppLayout } from "@/components/AppLayout";
const InteractiveConsole = lazy(() =>
  import("@/components/InteractiveConsole").then((m) => ({ default: m.InteractiveConsole })),
);
const MatrixEffect = lazy(() =>
  import("@/components/MatrixEffect").then((m) => ({ default: m.MatrixEffect })),
);
const PromotionBanner = lazy(() =>
  import("@/components/PromotionBanner").then((m) => ({ default: m.PromotionBanner })),
);
import { DeferUntilVisible } from "@/components/DeferUntilVisible";
import { useAuth } from "@/hooks/useAuth";
import { cn } from "@/lib/utils";
import {
  IntelCard,
  SectionEyebrow,
  SectionTitle,
  MetricCell,
  SeverityBadge,
  LiveTicker,
  StatusRail,
  TerminalChrome,
  Heatmap,
  IntelButton,
} from "@/components/intel";
const CVEIntelligence = lazy(() =>
  import("@/components/intel/CVEIntelligence").then((m) => ({ default: m.CVEIntelligence })),
);
import { SEOHead, OrganizationSchema, WebApplicationSchema, WebSiteSchema } from "@/components/seo";

const HomeBelowFold = lazy(() => import("@/components/home/HomeBelowFold"));

/* =================================================================
 * LOWKEYX // INTELLIGENCE CONSOLE v2 — Landing
 * Editorial bands · high-density technical layout.
 * ================================================================= */

const PUBLIC_TOOLS = ["domain_recon", "ip_intelligence"];

const osintTools = [
  { id: "domain_recon",    icon: ScanSearch,  code: "DOM_01", name: "Domain Recon",     desc: "WHOIS · DNS · SSL · Subdominios",        free: true,  tags: ["dns","whois","ssl"], accent: "primary" as const },
  { id: "ip_intelligence", icon: Radar,       code: "NET_02", name: "IP Intelligence",  desc: "Geo · ASN · Reputación · VPN/Proxy",     free: true,  tags: ["asn","geo","vpn"],  accent: "cyan"    as const },
  { id: "email_recon",     icon: AtSign,      code: "EML_03", name: "Email Recon",      desc: "Deep OSINT · Breaches · Plataformas",    free: false, tags: ["breach","osint"],   accent: "amber"   as const },
  { id: "email_hunter",    icon: MailSearch,  code: "EML_04", name: "Email Hunter",     desc: "Emails corporativos · Patrones",         free: false, tags: ["corp","pattern"],   accent: "primary" as const },
  { id: "username_search", icon: UserSearch,  code: "USR_05", name: "Username Search",  desc: "150+ plataformas · Validación HTTP",     free: false, tags: ["social","150+"],    accent: "cyan"    as const },
];

const utilityTools = [
  { icon: Binary,         code: "ENC_01", name: "Encoders",    desc: "Base64 · URL · HTML · JWT",  accent: "primary" as const },
  { icon: FileDigit,      code: "HSH_02", name: "Hash Lab",    desc: "MD5 · SHA · bcrypt · CRC32", accent: "amber"   as const },
  { icon: ScanLine,       code: "IMG_03", name: "Image OSINT", desc: "EXIF · Reverse · Metadata",  accent: "cyan"    as const },
  { icon: SquareTerminal, code: "TRM_04", name: "Terminal",    desc: "CLI con autocompletado",     accent: "primary" as const },
];

const intelFeed = [
  { sev: "critical" as const, id: "CVE-2025-31904", desc: "Remote code execution en orquestador de contenedores ampliamente desplegado.", tags: ["rce","container","unauth"], score: "9.8" },
  { sev: "high"     as const, id: "CVE-2025-31817", desc: "Escalado de privilegios local en kernel Linux ≤ 6.12.", tags: ["lpe","linux","kernel"], score: "8.7" },
  { sev: "high"     as const, id: "CVE-2025-31750", desc: "Bypass de autenticación en plugin SSO empresarial.", tags: ["auth-bypass","saml"], score: "8.4" },
  { sev: "medium"   as const, id: "CVE-2025-31702", desc: "SSRF a través de webhook en plataforma de CI/CD popular.", tags: ["ssrf","ci"], score: "6.9" },
  { sev: "info"     as const, id: "CVE-2025-31699", desc: "Information disclosure en endpoint de métricas no protegido.", tags: ["info-leak","metrics"], score: "5.3" },
];

const tickerItems = [
  { tone: "err"  as const, text: "● CVE-2025-31904 · CVSS 9.8 · RCE container orchestrator" },
  { tone: "warn" as const, text: "▲ Phishing wave detected · banking vertical · ES/PT" },
  { tone: "ok"   as const, text: "✓ All OSINT modules operational · latency < 800ms" },
  { tone: "info" as const, text: "ℹ New writeup published · HTB · Active Directory" },
  { tone: "err"  as const, text: "● CVE-2025-31817 · CVSS 8.7 · Linux kernel LPE" },
  { tone: "warn" as const, text: "▲ Suspicious VPN exit nodes flagged · 12 ASNs" },
  { tone: "ok"   as const, text: "✓ Knowledge base sync · 120+ articles indexed" },
];

const tracks = [
  {
    key: "operator",
    band: "OPERATOR",
    color: "text-primary",
    title: "Pista técnica",
    desc: "Para analistas, pentesters y curiosos con base. Reconocimiento, OSINT, simulaciones y writeups.",
    items: ["Reconocimiento OSINT en profundidad", "Terminal avanzada con autocompletado", "Writeups y vulnerabilidades", "Práctica con módulos reales"],
    cta: { label: "Acceder a Tools", to: "/arsenal" },
  },
  {
    key: "awareness",
    band: "AWARENESS",
    color: "text-signal-cyan",
    title: "Pista para personas",
    desc: "Para usuarios sin conocimiento técnico. Aprende a defenderte de phishing, fraudes y suplantaciones con simulaciones reales.",
    items: ["Simuladores de phishing y webs falsas", "Diccionario vivo de términos", "Retos guiados por nivel", "Lenguaje claro, sin jerga"],
    cta: { label: "Entrar a Conciencia", to: "/conciencia" },
  },
];

const Index = () => {
  const [matrixMode, setMatrixMode] = useState(false);
  const [showConsole, setShowConsole] = useState(false);
  const [showLandingPromo, setShowLandingPromo] = useState(false);
  const [showHeroConsole, setShowHeroConsole] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const consoleTimer = window.setTimeout(() => setShowHeroConsole(true), 350);
    const promoTimer = window.setTimeout(() => setShowLandingPromo(true), 900);

    return () => {
      clearTimeout(consoleTimer);
      clearTimeout(promoTimer);
    };
  }, []);

  const handleToolClick = (e: React.MouseEvent, toolId: string) => {
    if (!user && !PUBLIC_TOOLS.includes(toolId)) {
      e.preventDefault();
      navigate("/auth");
    }
  };

  const handleConsoleEasterEgg = (type: string) => {
    if (type === "matrix") setMatrixMode(true);
  };

  // Deterministic heatmap data so SSR/CSR match
  const heatmap = useMemo(() => {
    const arr: number[] = [];
    for (let i = 0; i < 14 * 7; i++) {
      const x = Math.sin(i * 12.9898 + 78.233) * 43758.5453;
      arr.push(Math.floor(Math.abs(x - Math.floor(x)) * 6));
    }
    return arr;
  }, []);

  return (
    <AppLayout>
      <SEOHead
        title="Plataforma OSINT y Conciencia Digital"
        description="Herramientas OSINT, terminal avanzada, academia de ciberseguridad y simuladores de conciencia digital para todos los niveles."
        canonical="/"
        keywords={["OSINT", "ciberseguridad", "hacking", "concienciación digital", "phishing", "herramientas OSINT"]}
      />
      <OrganizationSchema />
      <WebApplicationSchema />
      <WebSiteSchema />

      {matrixMode
        ? createPortal(
            <Suspense fallback={null}>
              <MatrixEffect active={matrixMode} onComplete={() => setMatrixMode(false)} />
            </Suspense>,
            document.body,
          )
        : null}

      {/* ============================================================
          BAND 00 · STATUS RAIL
      ============================================================ */}
      <StatusRail
        left={
          <>
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-signal-green intel-pulse-green" />
            <span className="hidden sm:inline">PLATFORM ONLINE</span>
            <span className="hidden md:inline text-border">|</span>
            <span className="hidden md:inline">ENV · PRODUCTION</span>
            <span className="hidden lg:inline text-border">|</span>
            <span className="hidden lg:inline">BUILD · v2.0</span>
          </>
        }
        right={
          <>
            <LiveClock />
          </>
        }
      />

      {showLandingPromo ? (
        <div className="container max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 pt-3">
          <Suspense fallback={null}>
            <PromotionBanner placement="landing" />
          </Suspense>
        </div>
      ) : null}

      {/* ============================================================
          BAND 01 · HERO
      ============================================================ */}
      <section className="relative overflow-hidden">
        <div className="container max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 pt-6 sm:pt-8 lg:pt-12 pb-16 lg:pb-24 relative z-10">
          <div className="grid lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-7">
              <div className="intel-eyebrow text-primary">
                <span>INTELLIGENCE RESEARCH PLATFORM</span>
              </div>

              <h1 className="intel-display mt-7 text-foreground text-[clamp(2.6rem,8vw,6.5rem)]">
                <span className="block">VE</span>
                <span className="block">
                  <span className="text-primary">LO QUE</span>
                  <span className="text-muted-foreground/40"> OTROS</span>
                </span>
                <span className="block">NO VEN.</span>
              </h1>

              <p className="mt-8 max-w-xl text-base sm:text-[15px] text-muted-foreground leading-[1.8]">
                Plataforma seria de hacking técnico y conciencia digital.
                Reconocimiento OSINT, terminal avanzada, academia y simulaciones reales —
                para operadores y para personas sin conocimiento previo.
              </p>

              <div className="mt-10 flex flex-wrap items-center gap-3">
                <Link to="/arsenal">
                  <IntelButton variant="primary">
                    Iniciar operación
                    <ArrowRight className="w-3.5 h-3.5" />
                  </IntelButton>
                </Link>
                <Link to="/conciencia">
                  <IntelButton variant="ghost">
                    Conciencia digital
                  </IntelButton>
                </Link>
              </div>

              <div className="mt-12 flex flex-wrap gap-x-8 gap-y-3">
                <Stat icon={Eye}  label="Sin rastros para los objetivos" />
                <Stat icon={Lock} label="Consultas anónimas privadas" />
                <Stat icon={Zap}  label="Latencia media < 800ms" />
              </div>
            </div>

            {/* Right: console + metric cells */}
            <div className="lg:col-span-5 space-y-3">
              <TerminalChrome
                title="lowkeyx@operator ~ ─ interactive console"
                footer={
                  <div className="flex items-center justify-between font-mono text-[9px] uppercase tracking-[0.16em] text-muted-foreground">
                    <span className="flex items-center gap-2">
                      <span className="inline-block w-1 h-1 rounded-full bg-signal-green intel-pulse-green" />
                      session · ready
                    </span>
                    <span>type <span className="text-primary">help</span></span>
                  </div>
                }
              >
                <div className="hidden sm:block">
                  {showHeroConsole ? (
                    <Suspense fallback={<div className="h-40 sm:h-52 lg:h-60" />}>
                      <InteractiveConsole onEasterEgg={handleConsoleEasterEgg} />
                    </Suspense>
                  ) : (
                    <div className="h-40 sm:h-52 lg:h-60 overflow-hidden font-mono text-[11px] sm:text-xs md:text-sm p-3 text-muted-foreground bg-gradient-to-b from-[hsl(0,5%,7%)] to-[hsl(0,5%,4%)]">
                      <div>&gt; LOWKEYX Terminal [Demo]</div>
                      <div>&gt; Inicializando consola…</div>
                      <div className="mt-3 text-foreground/70">$ help</div>
                      <div className="mt-1 inline-block text-primary animate-blink-caret">▌</div>
                    </div>
                  )}
                </div>
                <div className="sm:hidden">
                  {!showConsole ? (
                    <button
                      onClick={() => setShowConsole(true)}
                      className="w-full p-5 text-left group"
                    >
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-9 h-9 border border-primary/40 bg-primary/10 flex items-center justify-center">
                          <Terminal className="w-4 h-4 text-primary" />
                        </div>
                        <div className="flex-1">
                          <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-foreground">Tap para abrir</p>
                          <p className="text-xs text-muted-foreground mt-0.5">Comandos OSINT en tiempo real</p>
                        </div>
                        <ChevronRight className="w-4 h-4 text-muted-foreground group-active:translate-x-1 transition-transform" />
                      </div>
                      <div className="border border-border bg-surface-0 p-3 font-mono text-xs">
                        <span className="text-signal-green">$</span>{" "}
                        <span className="text-foreground/70">help</span>
                        <span className="ml-1 animate-blink-caret text-primary">▌</span>
                      </div>
                    </button>
                  ) : (
                    <Suspense fallback={<div className="h-40" />}>
                      <InteractiveConsole onEasterEgg={handleConsoleEasterEgg} />
                    </Suspense>
                  )}
                </div>
              </TerminalChrome>

              <div className="grid grid-cols-2 gap-px bg-border">
                <MetricCell label="Módulos OSINT"      value="08" unit="+" hint="↑ growing" />
                <MetricCell label="Plataformas user"   value="150" unit="+" hint="HTTP validated" />
                <MetricCell label="Writeups"           value="120" unit="+" hint="curated" />
                <MetricCell label="Simuladores"        value="06"           hint="awareness" />
              </div>
            </div>
          </div>
        </div>

        {/* Live ticker bottom of hero */}
        <LiveTicker items={tickerItems} />
      </section>

      <DeferUntilVisible
        minHeight={2400}
        rootMargin="250px"
        fallback={<div className="min-h-[2400px]" />}
      >
        <Suspense fallback={<div className="min-h-[2400px]" />}>
          <HomeBelowFold
            user={user}
            heatmap={heatmap}
            osintTools={osintTools}
            utilityTools={utilityTools}
            tracks={tracks}
            handleToolClick={handleToolClick}
          />
        </Suspense>
      </DeferUntilVisible>
    </AppLayout>
  );
};

/* ----------------------- Local helpers ----------------------- */

const Stat: React.FC<{ icon: React.ComponentType<any>; label: string }> = ({ icon: Icon, label }) => (
  <span className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
    <Icon className="w-3.5 h-3.5 text-primary" strokeWidth={1.7} />
    {label}
  </span>
);

// Isolated clock — re-renders only itself every second instead of the whole landing.
const LiveClock: React.FC = () => {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  const iso = now.toISOString();
  return (
    <>
      <span className="hidden sm:inline opacity-60">{iso.slice(0, 10)}</span>
      <span className="text-border hidden sm:inline">|</span>
      <span className="tabular-nums text-foreground/80">{iso.slice(11, 19)} UTC</span>
    </>
  );
};

export default Index;

/* =============== Tool box primitives (landing-only) =============== */

type AccentKey = "primary" | "cyan" | "amber";

const accentMap: Record<
  AccentKey,
  { text: string; bg: string; border: string; rail: string; soft: string }
> = {
  primary: {
    text: "text-primary",
    bg: "bg-primary/10",
    border: "border-primary/40",
    rail: "bg-primary",
    soft: "bg-primary/5",
  },
  cyan: {
    text: "text-signal-cyan",
    bg: "bg-signal-cyan/10",
    border: "border-signal-cyan/40",
    rail: "bg-signal-cyan",
    soft: "bg-signal-cyan/5",
  },
  amber: {
    text: "text-signal-amber",
    bg: "bg-signal-amber/10",
    border: "border-signal-amber/40",
    rail: "bg-signal-amber",
    soft: "bg-signal-amber/5",
  },
};

const statusToneMap: Record<"ok" | "warn" | "info", string> = {
  ok: "bg-signal-green text-surface-0",
  warn: "bg-signal-amber text-surface-0",
  info: "bg-primary text-white",
};

const ToolBox: React.FC<{
  index: number;
  code: string;
  name: string;
  desc: string;
  Icon: React.ComponentType<any>;
  accent: AccentKey;
  status: string;
  statusTone: "ok" | "warn" | "info";
  tags: string[];
}> = ({ index, code, name, desc, Icon, accent, status, statusTone, tags }) => {
  const a = accentMap[accent];
  return (
    <div className="relative h-full bg-surface-2 overflow-hidden group/tool transition-colors hover:bg-surface-3/60">
      {/* Left vertical accent rail */}
      <span className={cn("absolute left-0 top-0 bottom-0 w-[3px]", a.rail, "opacity-60 group-hover/tool:opacity-100 transition-opacity")} />
      {/* Sweep on hover */}
      <span className={cn("absolute inset-x-0 bottom-0 h-px", a.rail, "scale-x-0 group-hover/tool:scale-x-100 origin-left transition-transform duration-500")} />
      {/* Big outline number */}
      <span className="pointer-events-none select-none absolute -right-2 -top-3 font-display text-[88px] leading-none text-foreground/[0.04] tracking-tighter">
        {String(index).padStart(2, "0")}
      </span>

      <div className="relative p-5 sm:p-6 flex flex-col h-full">
        {/* Header row: code + status pill */}
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2 font-mono text-[9.5px] uppercase tracking-[0.2em] text-muted-foreground">
            <span className={cn("inline-block w-1.5 h-1.5 rounded-full", a.rail, "intel-pulse-red")} />
            {code}
          </div>
          <span className={cn(
            "font-mono text-[8.5px] uppercase tracking-[0.18em] px-1.5 py-0.5 leading-none",
            statusToneMap[statusTone],
          )}>
            {status}
          </span>
        </div>

        {/* Big icon block — angled clip */}
        <div className={cn(
          "relative w-[58px] h-[58px] flex items-center justify-center mb-5 border",
          a.border, a.bg, "clip-corner-mark",
          "group-hover/tool:scale-[1.04] transition-transform duration-300",
        )}>
          <Icon className={cn("w-7 h-7", a.text)} strokeWidth={1.5} />
          {/* Diagonal cross-marker */}
          <span className={cn("absolute -bottom-1 -right-1 w-2 h-2", a.rail)} />
        </div>

        <h3 className="font-display text-[17px] font-bold tracking-wide leading-tight mb-1.5">
          {name}
        </h3>
        <p className="text-[12.5px] text-muted-foreground leading-relaxed mb-4">{desc}</p>

        <div className="flex flex-wrap gap-1 mb-4">
          {tags.map((tag) => (
            <span
              key={tag}
              className="font-mono text-[8.5px] uppercase tracking-[0.12em] text-muted-foreground border border-border bg-surface-0 px-1.5 py-0.5"
            >
              {tag}
            </span>
          ))}
        </div>

        <div className="mt-auto pt-3 border-t border-border flex items-center justify-between font-mono text-[9px] uppercase tracking-[0.2em]">
          <span className="flex items-center gap-1.5 text-muted-foreground/80">
            <Activity className="w-3 h-3" /> &lt; 800ms
          </span>
          <span className={cn("flex items-center gap-1 transition-all duration-300", a.text, "translate-x-0 group-hover/tool:translate-x-1")}>
            EJECUTAR <ArrowRight className="w-3 h-3" />
          </span>
        </div>
      </div>
    </div>
  );
};

const UtilityBox: React.FC<{
  index: number;
  code: string;
  name: string;
  desc: string;
  Icon: React.ComponentType<any>;
  accent: AccentKey;
}> = ({ index, code, name, desc, Icon, accent }) => {
  const a = accentMap[accent];
  return (
    <div className="relative h-full bg-surface-2 overflow-hidden group/util hover:bg-surface-3/70 transition-colors">
      <span className={cn("absolute inset-x-0 top-0 h-[2px]", a.rail, "scale-x-0 group-hover/util:scale-x-100 origin-left transition-transform duration-400")} />
      <span className="pointer-events-none absolute -bottom-6 -right-2 font-display text-[110px] leading-none text-foreground/[0.04]">
        {String(index).padStart(2, "0")}
      </span>
      <div className="relative p-5 flex flex-col h-full">
        <div className="flex items-start justify-between mb-4">
          <div className="font-mono text-[9.5px] uppercase tracking-[0.2em] text-muted-foreground">{code}</div>
          <span className={cn("inline-block w-1.5 h-1.5 rounded-full", a.rail, "intel-pulse-red")} />
        </div>
        <div className={cn(
          "w-12 h-12 mb-4 flex items-center justify-center border",
          a.border, a.bg,
          "group-hover/util:rotate-[-4deg] transition-transform duration-300",
        )}>
          <Icon className={cn("w-6 h-6", a.text)} strokeWidth={1.5} />
        </div>
        <h4 className="font-display text-[15px] font-bold tracking-wide leading-tight">{name}</h4>
        <p className="text-[12px] text-muted-foreground mt-1.5 leading-relaxed flex-1">{desc}</p>
        <div className="mt-4 pt-3 border-t border-border flex items-center justify-between font-mono text-[9px] uppercase tracking-[0.2em] text-muted-foreground/70">
          <span>ABRIR</span>
          <span className={cn("flex items-center gap-1 transition-all", a.text, "opacity-60 group-hover/util:opacity-100 group-hover/util:translate-x-1")}>
            <ArrowRight className="w-3 h-3" />
          </span>
        </div>
      </div>
    </div>
  );
};